/**
 * The free market — the book, the reach, and the matching.
 *
 * Three rules carry the whole system, and each is stated once, here:
 *
 *   **Escrow.** A sell order's goods leave the treasury when the order is
 *   posted and come back on cancel or expiry — see the argument on
 *   `MarketOrder` in `markettypes.ts`. A buy order promises nothing until it
 *   fills, and fills only up to what the bidder can pay at that moment.
 *
 *   **Reach.** Two crowns may trade if either can reach the other: hops
 *   between their capitals on the combined road+lane graph, against
 *   `BASE_REACH_HOPS` plus a hull bonus. One graph walk per council, not one
 *   per order pair. War forbids the pair outright — a blockade needs no extra
 *   rule, being at war *is* the rule.
 *
 *   **You buy the cheapest.** Asks sort price-ascending (ties to the older
 *   order), bids price-descending (same tie), and every trade executes at the
 *   **sell** price. A buyer's limit is the most they were willing to pay, not
 *   what they pay; posting a high bid gets you served first, not fleeced.
 *
 * Matching draws no random number and never may: the book is hashed state, and
 * a market that settled differently on replay would desynchronise every save.
 */

import { RESOURCE_KEYS, RESOURCE_LABELS, type ResourceKey } from '../types.ts';
import { nationCapital } from '../../data/nations.ts';
import { fleetUpkeep } from './fleets.ts';
import { atWar, logLine, nationName } from './state.ts';
import type { CampaignState } from './types.ts';
import {
  BASE_REACH_HOPS,
  HULLS_PER_REACH,
  MARKET_PRICES,
  MAX_LIVE_ORDERS,
  MAX_ORDER_UNITS,
  MAX_PRICE_TENTHS,
  MIN_PRICE_TENTHS,
  ORDER_TTL_MONTHS,
  OrderKind,
  type MarketOrder,
} from './markettypes.ts';

// ── Reading the book ───────────────────────────────────────────────────────

export function findOrder(c: CampaignState, id: number): MarketOrder | undefined {
  return c.market.orders.find((o) => o.id === id);
}

/** One crown's open orders, in posting order. */
export function ordersOf(c: CampaignState, nation: number): MarketOrder[] {
  return c.market.orders.filter((o) => o.nation === nation);
}

/** Whether this crown already has this side of this good on the book. */
export function hasOrder(c: CampaignState, nation: number, kind: number, key: ResourceKey): boolean {
  return c.market.orders.some((o) => o.nation === nation && o.kind === kind && o.key === key);
}

/** Rival sell orders for a good — what the AI prices its own ask against. */
export function sellersOf(c: CampaignState, key: ResourceKey, except: number): number {
  let n = 0;
  for (const o of c.market.orders) {
    if (o.kind === OrderKind.Sell && o.key === key && o.nation !== except) n++;
  }
  return n;
}

// ── Posting and cancelling ─────────────────────────────────────────────────

/**
 * Why this order may not be posted, or the empty string.
 *
 * A sentence rather than a boolean, the pattern every campaign panel already
 * leans on: the UI greys the button with the reason, and the command handler is
 * the second line of defence.
 */
export function postRefusal(
  c: CampaignState,
  nation: number,
  kind: number,
  key: ResourceKey,
  amount: number,
  price: number,
): string {
  const n = c.nations[nation];
  if (!n || !n.alive) return 'There is nobody to trade for.';
  if (MARKET_PRICES[key] === undefined) return `Nobody on the book deals in ${RESOURCE_LABELS[key]}.`;
  // Finiteness first, and explicitly: every gate below is written as a
  // rejection comparison, and every comparison with NaN is false — so without
  // this line a NaN amount walks through all of them and lands in a hashed
  // treasury, where JSON writes it as null and the next load reads 0. The
  // book is hashed state; nothing non-finite may set foot on it.
  if (!Number.isFinite(amount) || !Number.isFinite(price)) {
    return 'The book does not carry that order.';
  }
  if (Math.round(amount) < 1) return 'An order needs at least one unit on it.';
  if (Math.round(amount) > MAX_ORDER_UNITS) {
    return `No single order carries more than ${MAX_ORDER_UNITS} units.`;
  }
  if (Math.round(price) < MIN_PRICE_TENTHS || Math.round(price) > MAX_PRICE_TENTHS) {
    return 'No market will carry that price.';
  }
  if (ordersOf(c, nation).length >= MAX_LIVE_ORDERS) {
    return `The book carries ${MAX_LIVE_ORDERS} orders a crown, no more.`;
  }
  if (kind === OrderKind.Sell && n.treasury[key] < Math.round(amount)) {
    return `The treasury holds ${Math.floor(n.treasury[key])} ${RESOURCE_LABELS[key]}, not ${Math.round(amount)}.`;
  }
  return '';
}

/**
 * Put goods on the table. The amount leaves the treasury *now* — that is the
 * escrow rule, argued at `MarketOrder` — and comes back on cancel or expiry.
 * Returns null and moves nothing when it cannot be done.
 */
export function tryPostSell(
  c: CampaignState,
  nation: number,
  key: ResourceKey,
  amount: number,
  price: number,
): MarketOrder | null {
  const units = Math.round(amount);
  const tenths = Math.round(price);
  if (postRefusal(c, nation, OrderKind.Sell, key, units, tenths) !== '') return null;
  c.nations[nation].treasury[key] -= units;
  return pushOrder(c, nation, OrderKind.Sell, key, units, tenths);
}

/**
 * Post a standing bid. No gold moves until a match lands — the other half of
 * the escrow argument — so the only gates are the book's own.
 */
export function tryPostBuy(
  c: CampaignState,
  nation: number,
  key: ResourceKey,
  amount: number,
  price: number,
): MarketOrder | null {
  const units = Math.round(amount);
  const tenths = Math.round(price);
  if (postRefusal(c, nation, OrderKind.Buy, key, units, tenths) !== '') return null;
  return pushOrder(c, nation, OrderKind.Buy, key, units, tenths);
}

function pushOrder(
  c: CampaignState,
  nation: number,
  kind: number,
  key: ResourceKey,
  amount: number,
  price: number,
): MarketOrder {
  const o: MarketOrder = {
    id: c.market.nextOrderId++,
    nation,
    kind,
    key,
    amount,
    price,
    placedTurn: c.turn,
  };
  c.market.orders.push(o);
  return o;
}

/**
 * Take an order off the book. Only the crown that posted it may, and a sell's
 * escrow comes home in full.
 */
export function cancelOrder(c: CampaignState, nation: number, id: number): boolean {
  const i = c.market.orders.findIndex((o) => o.id === id);
  if (i < 0) return false;
  const o = c.market.orders[i];
  if (o.nation !== nation) return false;
  refundEscrow(c, o);
  c.market.orders.splice(i, 1);
  return true;
}

/** The escrow, home. Sells only; a bid never held anything. */
function refundEscrow(c: CampaignState, o: MarketOrder): void {
  if (o.kind !== OrderKind.Sell) return;
  const n = c.nations[o.nation];
  if (n) n.treasury[o.key] += o.amount;
}

// ── Reach ──────────────────────────────────────────────────────────────────

/**
 * The road and lane graphs as one adjacency list — a merchant's cart rolls and
 * his goods sail, so distance for trade is hops on the union. Built and thrown
 * away by the caller, the same discipline `laneAdjacency` keeps: holding on to
 * it would be a cache of two mutable graphs.
 */
export function combinedAdjacency(c: CampaignState): number[][] {
  const adj: number[][] = c.provinces.map(() => []);
  for (const l of c.links) {
    adj[l.a].push(l.b);
    adj[l.b].push(l.a);
  }
  for (const l of c.lanes) {
    adj[l.a].push(l.b);
    adj[l.b].push(l.a);
  }
  return adj;
}

/** Breadth-first hops from one province over a prebuilt adjacency. */
function hopsOver(adj: number[][], from: number): number[] {
  const dist = new Array<number>(adj.length).fill(-1);
  if (from < 0 || from >= adj.length) return dist;
  dist[from] = 0;
  const queue = [from];
  for (let head = 0; head < queue.length; head++) {
    const at = queue[head];
    for (const n of adj[at]) {
      if (dist[n] !== -1) continue;
      dist[n] = dist[at] + 1;
      queue.push(n);
    }
  }
  return dist;
}

/**
 * The crown's trading seat: its historic capital from the data table. Fixed on
 * purpose — a crown driven from its seat has larger problems than its bourse,
 * and a reach that jumped around with the front line would make the market's
 * geography unreadable.
 */
export function marketSeat(c: CampaignState, nation: number): number {
  return nationCapital(c.nations[nation]?.def ?? nation);
}

/**
 * How far this crown's merchants range, in hops. `fleetUpkeep` is the hull
 * count — the function is named for the bill it prices, but what it returns is
 * hulls, and hulls are exactly what a merchant convoy borrows.
 */
export function nationReach(c: CampaignState, nation: number): number {
  return BASE_REACH_HOPS + Math.floor(fleetUpkeep(c, nation) / HULLS_PER_REACH);
}

/** Hops between two crowns' seats on the combined graph, or -1. */
export function capitalDistance(c: CampaignState, a: number, b: number): number {
  const d = hopsOver(combinedAdjacency(c), marketSeat(c, a));
  return d[marketSeat(c, b)] ?? -1;
}

/**
 * Hops from one crown's seat to **every** province, in one walk.
 *
 * `capitalDistance` builds the adjacency and runs a breadth-first search per
 * call, which is the right shape for a single question and the wrong shape for
 * "who on this board can I trade with" — a panel asking that of forty-two
 * crowns would do forty-two of them, on a screen that repaints whenever the
 * realm moves. This is the same walk `matchMarket` does per council, exported
 * so a reader can ask once and index the answer with `marketSeat`.
 */
export function seatHops(c: CampaignState, nation: number): number[] {
  return hopsOver(combinedAdjacency(c), marketSeat(c, nation));
}

/**
 * Whether two crowns may trade at all: both alive, at peace, and within reach
 * of **either** — a big navy carries the goods both ways.
 *
 * The one-off convenience form; `matchMarket` builds the same answer for every
 * pair at once and does not call this.
 */
export function mayTradePair(c: CampaignState, a: number, b: number): boolean {
  if (a === b || a < 0 || b < 0) return false;
  if (!c.nations[a]?.alive || !c.nations[b]?.alive) return false;
  if (atWar(c, a, b)) return false;
  const d = capitalDistance(c, a, b);
  if (d < 0) return false;
  return d <= nationReach(c, a) || d <= nationReach(c, b);
}

// ── Housekeeping ───────────────────────────────────────────────────────────

/**
 * Sweep the book: dead crowns' orders are cancelled, stale orders lapse, and
 * every escrow swept off comes home. Runs before the AI posts, so the slots it
 * frees are usable the same council.
 */
export function expireOrders(c: CampaignState): void {
  const book = c.market;
  for (let i = book.orders.length - 1; i >= 0; i--) {
    const o = book.orders[i];
    const dead = !(c.nations[o.nation]?.alive ?? false);
    const stale = c.turn - o.placedTurn >= ORDER_TTL_MONTHS;
    if (!dead && !stale) continue;
    refundEscrow(c, o);
    if (!dead && o.nation === c.playerNation) {
      logLine(
        c,
        o.kind === OrderKind.Sell
          ? `Our ${o.amount} ${RESOURCE_LABELS[o.key]} come home unsold.`
          : `Our bid for ${RESOURCE_LABELS[o.key]} lapses unfilled.`,
      );
    }
    book.orders.splice(i, 1);
  }
}

// ── Matching ───────────────────────────────────────────────────────────────

/**
 * Settle the book, once, now.
 *
 * Per good, in `RESOURCE_KEYS` order: asks price-ascending then id-ascending,
 * bids price-descending then id-ascending, matched greedily while the bid
 * covers the ask and the pair is within reach and at peace. Trades execute at
 * the ask — the buyer walks the book from the cheap end, which is the brief's
 * "you buy the cheapest" — and a bid pays only what its crown can actually
 * afford at this moment, the re-validation half of the escrow argument.
 *
 * Everything here is a sort over hashed state and integer arithmetic on
 * amounts; there is no RNG and there must never be one.
 */
export function matchMarket(c: CampaignState): void {
  const book = c.market;
  if (book.orders.length === 0) return;

  /**
   * Reach, precomputed per council rather than per order pair: one BFS per
   * crown that is actually on the book, over one shared adjacency.
   */
  const adj = combinedAdjacency(c);
  const hops = new Map<number, number[]>();
  const hopsFor = (n: number): number[] => {
    const hit = hops.get(n);
    if (hit) return hit;
    const d = hopsOver(adj, marketSeat(c, n));
    hops.set(n, d);
    return d;
  };
  const reach = c.nations.map((_, i) => nationReach(c, i));
  const pairOk = (a: number, b: number): boolean => {
    if (!c.nations[a]?.alive || !c.nations[b]?.alive) return false;
    if (atWar(c, a, b)) return false;
    const d = hopsFor(a)[marketSeat(c, b)] ?? -1;
    if (d < 0) return false;
    return d <= reach[a] || d <= reach[b];
  };

  for (const key of RESOURCE_KEYS) {
    const sells = book.orders
      .filter((o) => o.kind === OrderKind.Sell && o.key === key && o.amount > 0)
      .sort((x, y) => x.price - y.price || x.id - y.id);
    if (sells.length === 0) continue;
    const buys = book.orders
      .filter((o) => o.kind === OrderKind.Buy && o.key === key && o.amount > 0)
      .sort((x, y) => y.price - x.price || x.id - y.id);

    for (const buy of buys) {
      const buyer = c.nations[buy.nation];
      if (!buyer) continue;
      for (const sell of sells) {
        if (buy.amount <= 0) break;
        if (sell.amount <= 0) continue;
        // Asks are sorted ascending: once one is over the bid, they all are.
        if (buy.price < sell.price) break;
        if (sell.nation === buy.nation) continue;
        if (!pairOk(sell.nation, buy.nation)) continue;

        // Affordability in integer tenths, not float gold-per-unit: the book
        // is fixed-point for a reason, and `4 / 0.1` is 39.999… in binary —
        // a floor over that short-fills a buyer who can pay exactly.
        const affordable = Math.floor((buyer.treasury.gold * 10) / sell.price);
        // Out of gold: this bid is done for the month, not cancelled — it may
        // be payable again by the next council.
        if (affordable <= 0) break;
        const qty = Math.min(buy.amount, sell.amount, affordable);
        if (qty <= 0) break;

        const gold = (qty * sell.price) / 10;
        buyer.treasury.gold -= gold;
        c.nations[sell.nation].treasury.gold += gold;
        // The goods were escrowed at posting; they exist only on the order.
        buyer.treasury[key] += qty;
        buy.amount -= qty;
        sell.amount -= qty;

        if (buy.nation === c.playerNation || sell.nation === c.playerNation) {
          const price = (sell.price / 10).toFixed(1);
          logLine(
            c,
            buy.nation === c.playerNation
              ? `Bought ${qty} ${RESOURCE_LABELS[key]} off ${nationName(c, sell.nation)} at ${price} gold.`
              : `Sold ${qty} ${RESOURCE_LABELS[key]} to ${nationName(c, buy.nation)} at ${price} gold.`,
          );
        }
      }
    }
  }

  book.orders = book.orders.filter((o) => o.amount > 0);
}
